import { useTranslation } from "react-i18next";
import type { RuleSet } from "styled-components";
import { css, styled } from "styled-components";

import { Placeholder } from "shared/atoms/Placeholder";

import { BodySmall, H4, Overline3 } from "./styles";

type ActivityType = "exercise" | "text_lesson";

interface Props {
  image_url: string | null;
  isLoading?: boolean;
  subtitle?: string;
  title: string;
  type: ActivityType;
}

export const ActivityItem: React.FC<Props> = ({ image_url, isLoading, subtitle, title, type }) => {
  const { t } = useTranslation();

  const getActivityTitle = () => {
    if (type === "exercise") return t("common.exercise");
    if (type === "text_lesson") return t("common.lesson");
    return "";
  };

  return (
    <Container>
      <ImageContainer>
        {image_url ? (
          <img src={image_url} alt={title} />
        ) : (
          <Placeholder fontSize="80px" isLoading={isLoading} type="image" />
        )}
      </ImageContainer>

      <div>
        <StyledOverline3 $type={type}>{getActivityTitle()}</StyledOverline3>
        <H4>{title}</H4>
        {subtitle && <StyledBodySmall>{subtitle}</StyledBodySmall>}
      </div>
    </Container>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 340px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(39, 53, 70, 0.5);
  z-index: 999;
`;

const ImageContainer = styled.div`
  max-height: 80px;
  margin-right: 12px;

  & > img {
    height: 100%;
  }
`;

const styles: Record<ActivityType, RuleSet> = {
  exercise: css`
    color: ${({ theme }) => theme.colors.redesign.o100};
  `,
  text_lesson: css`
    color: ${({ theme }) => theme.colors.redesign.t100};
  `,
};

const StyledBodySmall = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.redesign.db70};
`;

const StyledOverline3 = styled(Overline3)<{ $type: ActivityType }>`
  margin-bottom: 4px;
  text-transform: uppercase;
  ${({ $type }) => styles[$type]}
`;
